import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Megaphone, Plus, Trash2, Send, Clock, Check, Ban, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { Page, PageHeader, Card, CardHeader, btnPrimary, btnSecondary, btnGhost, EmptyState, SkeletonRows, hoverActions } from '@/components/rm'
import { Modal } from '@/components/rm/form'
import { useMarketing, useEmailableTotal, useAudienceCount, type MarketingTemplate } from '@/hooks/use-marketing'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/marketing')({ component: MarketingPage })

const STATUS: Record<string, { label: string; cls: string; icon: typeof Check }> = {
  draft: { label: 'Draft', cls: 'bg-canvas-subtle text-token-secondary', icon: Clock },
  scheduled: { label: 'Scheduled', cls: 'bg-token-info-surface text-token-info', icon: Clock },
  sending: { label: 'Sending', cls: 'bg-token-warning-surface text-token-warning', icon: Send },
  sent: { label: 'Sent', cls: 'bg-token-success-surface text-token-success', icon: Check },
  cancelled: { label: 'Cancelled', cls: 'bg-canvas-subtle text-token-muted', icon: Ban },
}

const PACKS = [
  { id: 'small' as const, emails: 500, blurb: 'a few campaigns' },
  { id: 'medium' as const, emails: 2000, blurb: 'a year of monthly newsletters' },
  { id: 'large' as const, emails: 5000, blurb: 'best value per email' },
]

function MarketingPage() {
  const navigate = useNavigate()
  const { campaigns, templates, usage, isLoading, save, remove, buyCredits, removeTemplate, duplicate } = useMarketing()
  const { data: emailable } = useEmailableTotal()
  const { data: reach } = useAudienceCount({ all: true })
  const { role } = useAuth()
  const [picking, setPicking] = useState(false)
  const [buying, setBuying] = useState(false)

  const start = async (t: MarketingTemplate | null) => {
    try {
      const id = await save.mutateAsync({
        name: t ? t.name : 'Untitled campaign',
        subject: t?.subject ?? '',
        design: t?.design ?? null,
        audience: { all: true },
      })
      setPicking(false)
      navigate({ to: '/marketing/$id', params: { id } })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not create the campaign') }
  }

  const buy = async (pack: 'small' | 'medium' | 'large') => {
    try { window.location.href = await buyCredits.mutateAsync(pack) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Could not start checkout') }
  }

  const unsubscribed = (emailable ?? 0) - (reach ?? 0)

  return (
    <Page>
      <PageHeader
        eyebrow="Reach your clients"
        title="Marketing"
        subtitle="Newsletters, announcements and offers to your client list. Anyone who unsubscribes is excluded automatically."
        actions={<button className={btnPrimary} onClick={() => setPicking(true)}><Plus className="h-4 w-4" /> New campaign</button>}
      />

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Card className="p-5">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Emails this month</p>
          <p className="font-display text-[30px] font-bold tabular-nums text-token-primary">
            {usage ? usage.sendable.toLocaleString() : '—'}
          </p>
          <p className="mt-1 text-[12.5px] text-token-secondary">
            {usage
              ? `${Math.max(0, usage.free_allowance - usage.free_used)} free left of ${usage.free_allowance}${usage.credits > 0 ? ` · ${usage.credits.toLocaleString()} credits` : ''}`
              : 'loading'}
          </p>
          {role === 'company-admin' && (
            <button className={cn(btnSecondary, 'mt-4 w-full')} onClick={() => setBuying(true)}>Buy more emails</button>
          )}
        </Card>

        <Card className="p-5">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Your reach</p>
          <p className="font-display text-[30px] font-bold tabular-nums text-token-primary">{(reach ?? 0).toLocaleString()}</p>
          {/* Showing what was excluded, not just the total. A number that quietly shrinks is the
              thing that makes people distrust the tool. */}
          <p className="mt-1 text-[12.5px] text-token-secondary">
            clients with an email address
            {unsubscribed > 0 && <> · <span className="text-token-warning">{unsubscribed} unsubscribed</span></>}
          </p>
        </Card>

        <Card className="p-5">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Campaigns sent</p>
          <p className="font-display text-[30px] font-bold tabular-nums text-token-primary">
            {campaigns.filter((c) => c.status === 'sent').length}
          </p>
          <p className="mt-1 text-[12.5px] text-token-secondary">all time</p>
        </Card>
      </div>

      <Card className="mt-5">
        <CardHeader title="Campaigns" count={campaigns.length} />
        {isLoading ? <SkeletonRows /> : campaigns.length === 0 ? (
          <EmptyState label="No campaigns yet. Send your clients a newsletter or an announcement." cta="New campaign" onCta={() => setPicking(true)} />
        ) : (
          <ul className="divide-y divide-token-subtle">
            {campaigns.map((c) => {
              const s = STATUS[c.status] ?? STATUS.draft
              const Icon = s.icon
              return (
                <li key={c.id} className="group flex items-center justify-between px-5 py-3 hover:bg-token-surface">
                  <Link to="/marketing/$id" params={{ id: c.id }} className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-token-selected text-token-success"><Megaphone className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-semibold text-token-primary">{c.name}</p>
                      <p className="truncate text-[12px] text-token-secondary">{c.subject || 'No subject yet'}</p>
                    </div>
                  </Link>
                  <div className="flex flex-none items-center gap-3">
                    <span className={cn('inline-flex items-center gap-1.5 rounded-[7px] px-2 py-1 text-[11.5px] font-bold', s.cls)}>
                      <Icon className="h-3 w-3" /> {s.label}
                    </span>
                    <div className={cn('flex items-center gap-1', hoverActions)}>
                      <button className={btnGhost} title="Duplicate into a new draft"
                        onClick={async () => {
                          try { navigate({ to: '/marketing/$id', params: { id: await duplicate.mutateAsync(c.id) } }) }
                          catch (e) { toast.error(e instanceof Error ? e.message : 'Could not duplicate') }
                        }}><Copy className="h-4 w-4" /></button>
                      {c.status === 'draft' && (
                        <button className={btnGhost} title="Delete"
                          onClick={() => remove.mutate(c.id)}><Trash2 className="h-4 w-4 text-rose-400" /></button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Modal open={picking} onClose={() => setPicking(false)} title="Start a campaign" wide
        footer={<button className={btnSecondary} onClick={() => setPicking(false)}>Cancel</button>}>
        {templates.some((t) => t.company_id) && (
          <>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Your templates</p>
            <div className="mb-5 grid gap-3 sm:grid-cols-2">
              {templates.filter((t) => t.company_id).map((t) => (
                <div key={t.id} className="group relative rounded-[12px] border border-token-subtle transition hover:border-token-strong">
                  <button onClick={() => start(t)} className="w-full p-4 text-left">
                    <p className="text-[14px] font-bold text-token-primary">{t.name}</p>
                    <p className="mt-1 text-[12.5px] text-token-secondary">{t.subject || 'Your saved template'}</p>
                  </button>
                  <button
                    onClick={() => removeTemplate.mutate(t.id)} title="Delete this template"
                    className="absolute right-2 top-2 rounded-[7px] p-1.5 opacity-0 transition group-hover:opacity-100 hover:bg-canvas-subtle">
                    <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                  </button>
                </div>
              ))}
            </div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Start from a layout</p>
          </>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {templates.filter((t) => !t.company_id).map((t) => (
            <button key={t.id} onClick={() => start(t)}
              className="rounded-[12px] border border-token-subtle p-4 text-left transition hover:border-token-strong hover:bg-token-surface">
              <p className="text-[14px] font-bold text-token-primary">{t.name}</p>
              {t.description && <p className="mt-1 text-[12.5px] text-token-secondary">{t.description}</p>}
            </button>
          ))}
          <button onClick={() => start(null)}
            className="rounded-[12px] border border-dashed border-token-subtle p-4 text-left transition hover:border-token-strong">
            <p className="text-[14px] font-bold text-token-primary">Start from scratch</p>
            <p className="mt-1 text-[12.5px] text-token-secondary">A blank canvas.</p>
          </button>
        </div>
      </Modal>

      <Modal open={buying} onClose={() => setBuying(false)} title="Buy marketing emails"
        footer={<button className={btnSecondary} onClick={() => setBuying(false)}>Cancel</button>}>
        <div className="space-y-3">
          <p className="text-[13px] text-token-secondary">
            You get {usage?.free_allowance ?? 100} free marketing emails every month. Bought emails never expire and are
            only used once the free ones are gone.
          </p>
          {PACKS.map((p) => (
            <button key={p.id} onClick={() => buy(p.id)}
              className="flex w-full items-center justify-between rounded-[12px] border border-token-subtle px-4 py-3 text-left transition hover:border-token-strong">
              <span>
                <span className="block text-[15px] font-bold text-token-primary">{p.emails.toLocaleString()} emails</span>
                <span className="block text-[12.5px] text-token-secondary">{p.blurb}</span>
              </span>
              <span className="text-[13px] font-semibold text-token-info">Buy →</span>
            </button>
          ))}
        </div>
      </Modal>
    </Page>
  )
}
